import { APITypesV1 as APITypes } from "@cur8/api-client";
import { FrameSequenceURI } from "@cur8/uri";
import { loadImage } from "lib/loader";
import { useEffect, useState } from "react";
import { useAPIClient } from "render/context/APIContext";
import { PanoramaImageURI } from "../../lib/uri";

const MAX_RETRIES = 10; // Retrying on 404, since upload not always available directly after upload (and we try to fetch immediately...)

function parseUri(targetUri: string) {
  const tokens = targetUri.split(":");
  return { filenameAndPath: tokens.pop() || "", externalId: tokens[2] };
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type OriginalSize = {
  w: number;
  h: number;
} | null;

export function useAnnotationImage(
  annotation: APITypes.Annotation,
  isOverview: boolean
) {
  const api = useAPIClient();
  const [image, setImage] = useState<HTMLImageElement>();
  const [originalSize, setOriginalSize] = useState<OriginalSize>();

  useEffect(() => {
    async function fetchDermaImage() {
      const { filenameAndPath, externalId } = parseUri(annotation.targetUri);
      let path = filenameAndPath;

      // targetUri could reference another annotation, in which case the applicationSpecificTarget contains the filename and path
      if (
        !filenameAndPath.includes(".") &&
        annotation.applicationSpecificTarget
      ) {
        const { filenameAndPath } = parseUri(
          annotation.applicationSpecificTarget
        );
        path = filenameAndPath;
      }

      let response = await api.blob.fetchPatientBlob({
        patientId: externalId,
        path,
      }).result;
      let nRetries = 0;
      while (response.status !== 200 && ++nRetries < MAX_RETRIES) {
        console.warn(`Could not fetch ${filenameAndPath}, retrying ...`);
        await wait(1000);
        response = await api.blob.fetchPatientBlob({
          patientId: externalId,
          path,
        }).result;
      }
      const blob = await response.blob();
      return { blob: blob, originalSize: null };
    }

    async function fetchRigImage() {
      if (annotation.applicationSpecificTarget?.startsWith("panorama:")) {
        return fetchPanoramaImage();
      } else if (annotation.applicationSpecificTarget?.startsWith("rgbino:")) {
        return fetchRgbinoImage();
      } else {
        console.error(
          "unknown applicationSpecificTarget in annotation: ",
          annotation
        );
      }
    }

    async function fetchRgbinoImage() {
      const uri = FrameSequenceURI.parse(annotation.targetUri);
      if (uri) {
        const rect = (annotation.data as APITypes.AnnotationData2D).rect;
        const { blob, meta } = await api.transcode.fetchFrame(
          {
            deviceId: uri.deviceId,
            recordingId: uri.recordingId,
            trackId: uri.trackId,
            frameId: uri.frameId,
          },
          isOverview
            ? undefined
            : {
                x: rect.left,
                y: rect.top,
                w: rect.width,
                h: rect.height,
              },
          undefined
        ).result;
        return { blob: blob, originalSize: meta.original.size };
      }
    }

    async function fetchPanoramaImage() {
      if (!annotation.applicationSpecificTarget) {
        console.error("no applicationSpecificTarget");
        return;
      }

      const rect = (annotation.data as APITypes.AnnotationData2D).rect;

      const uri = PanoramaImageURI.fromString(
        annotation.applicationSpecificTarget
      );
      const { blob, meta } = await api.transcode.fetchPanorama(
        {
          deviceId: uri.deviceId,
          recordingId: uri.recordingId,
          side: uri.side,
          cameraName: uri.cameraName,
        },
        isOverview
          ? undefined
          : {
              x: rect.left,
              y: rect.top,
              w: rect.width,
              h: rect.height,
            },
        { w: 1024, h: 1024 }
      ).result;

      return { blob: blob, originalSize: meta.original.size };
    }

    const annoImageFetch = annotation.targetUri.startsWith("cur8:recording")
      ? fetchRigImage
      : fetchDermaImage;

    annoImageFetch()
      .then(async (res) => {
        if (!res) throw new Error("Could not fetch image");
        const url = URL.createObjectURL(res.blob);
        const image = await loadImage(url);
        return { image: image, originalSize: res.originalSize };
      })
      .then((res) => {
        setImage(res.image);
        setOriginalSize(res.originalSize);
      })
      .catch(console.error);
  }, [api, annotation, isOverview]);

  return { image: image, originalSize: originalSize };
}
